import { orientationsFromConfig } from "../components/config/orientations";
import { saveOrientationShading, sceneShadingKey } from "../components/config/orientationEvidence";
import { needsScenePreparation, prepareSceneSimulation } from "./prepareSceneSimulation";
import { scenePreparationIssue } from "./scenePreparationIssue";
import { sceneShadingPending } from "../components/config/sceneSimulationIssues";
import { prepareSceneInBackground } from "../services/sceneCalculation";
import { detailedSceneKey } from "./detailedShading";

// The same measurement carried over from the layout as it was: the same
// calculation time or, for a table stored without one, the same table.
function sameMeasurement(a, b) {
  if (!a || !b) return false;
  if (a.measuredAt || b.measuredAt) return a.measuredAt === b.measuredAt;
  return JSON.stringify(a.table ?? null) === JSON.stringify(b.table ?? null);
}

// Invalidate evidence from an older geometry. Interactive saves opt into the
// shared calculation/progress overlay; fresh results are reused. Failed or
// cancelled shading leaves the layout saveable, with explicit pending evidence.
// Detailed shading prepares the validated geometry here; its hourly electrical
// calculation still belongs to the backend simulation.
export async function prepareLayoutShading(previous, config, { orientationId, placement, getModule, calculate = false, ...options }) {
  let next = config;
  if (placement !== undefined) {
    const plane = orientationsFromConfig(next).find((p) => p.id === orientationId);
    const saved = plane?.primary ? next.pv_design : plane;
    const { placement: oldPlacement, ...evidence } = saved?.obstacleShading || {};
    const moved = Boolean(evidence.table) && JSON.stringify(oldPlacement ?? null) !== JSON.stringify(placement ?? null);
    next = saveOrientationShading(next, orientationId, {
      ...evidence, ...(placement ? { placement } : {}), ...(moved ? { stale: true } : {}),
    });
  }
  if (sceneShadingKey(previous) !== sceneShadingKey(next)) {
    const before = orientationsFromConfig(previous);
    orientationsFromConfig(next).forEach((plane) => {
      const saved = plane.primary ? next.pv_design : plane;
      const old = before.find((p) => p.id === plane.id);
      const oldSaved = old ? (old.primary ? previous.pv_design : old) : null;
      if (saved?.obstacleShading && sameMeasurement(saved.obstacleShading, oldSaved?.obstacleShading)) {
        next = saveOrientationShading(next, plane.id, { ...saved.obstacleShading, stale: true });
      }
    });
  }
  const detailed = next.pvDetailedShading?.enabled === true;
  const pending = sceneShadingPending(next);
  const scene = next.pvDetailedShading?.scene;
  const detailedCurrent = scene?.version === 1 && Array.isArray(scene.moduleDimensions) &&
    scene.key === sceneShadingKey(next) + detailedSceneKey(next, scene.moduleDimensions);
  if (!needsScenePreparation(next) || (!detailed && (!calculate || !pending.length)) || (calculate && detailed && detailedCurrent)) {
    return { config: next };
  }
  try {
    const module = await getModule();
    let prepared = await (calculate ? prepareSceneInBackground : prepareSceneSimulation)(next, { ...options, module });
    if (calculate && !detailed && sceneShadingPending(prepared).length) {
      throw new Error("Scene calculation did not return current shading for every surface.");
    }
    orientationsFromConfig(prepared).forEach(plane => {
      const evidence = (plane.primary ? prepared.pv_design : plane)?.obstacleShading;
      if (evidence?.preparationIssue) {
        const { preparationIssue, ...clean } = evidence;
        prepared = saveOrientationShading(prepared, plane.id, clean);
      }
    });
    return { config: prepared, ...(calculate ? { shadingUpdated: true } : {}) };
  } catch (error) {
    const shadingIssue = scenePreparationIssue(error);
    // An old preview must not claim success after the current scene failed.
    orientationsFromConfig(next).forEach((plane, index) => {
      if (!detailed && !pending.includes(index + 1)) return;
      const evidence = (plane.primary ? next.pv_design : plane)?.obstacleShading;
      next = saveOrientationShading(next, plane.id, { ...evidence, stale: true, preparationIssue: shadingIssue });
    });
    if (next.pvDetailedShading?.enabled) next = { ...next, pvDetailedShading: { ...next.pvDetailedShading, scene: undefined } };
    return { config: next, shadingError: error?.message || String(error), shadingIssue };
  }
}
